import { useCallback, useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface UseToastStateReturn {
  /** Current list of active toasts (already trimmed to maxToasts) */
  toasts: Toast[];
  /** Add a toast; assigns an id and schedules its per-type auto-dismiss */
  showToast: (toast: Omit<Toast, 'id'>) => void;
  /** Remove a toast from the list immediately (no exit animation) */
  dismissToast: (id: string) => void;
}

const DEFAULT_MAX_TOASTS = 5;
const DEFAULT_ERROR_DURATION_MS = 5000;
const DEFAULT_DURATION_MS = 3000;

/**
 * Shared toast provider state: id generation, per-type auto-dismiss
 * duration, and maxToasts trimming.
 *
 * This is the single state machine behind both the Tailwind (`Toast.tsx`)
 * and inline-styles (`ToastInline.tsx`) toast implementations — they are
 * thin renderers over this hook so behavior (timing, ids, trimming) can't
 * drift between the two.
 *
 * Note: auto-dismiss (the timer started here) removes the toast from the
 * list immediately, with no exit animation — only a user-triggered
 * dismiss (see `useToastItemExit`) plays the fade/slide-out first. This
 * matches the pre-existing behavior of both toast implementations.
 */
export function useToastState(maxToasts: number = DEFAULT_MAX_TOASTS): UseToastStateReturn {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = { ...toast, id };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Keep only the most recent toasts
        return updated.slice(-maxToasts);
      });

      // Auto-dismiss after duration
      const duration = toast.duration ?? (toast.type === 'error' ? DEFAULT_ERROR_DURATION_MS : DEFAULT_DURATION_MS);
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    },
    [maxToasts, dismissToast],
  );

  return { toasts, showToast, dismissToast };
}

const EXIT_ANIMATION_DURATION_MS = 200;

export interface UseToastItemExitReturn {
  /** Whether the item's exit animation should currently be shown */
  isExiting: boolean;
  /** Trigger the exit animation, then remove the toast after it completes */
  handleDismiss: () => void;
}

/**
 * Per-toast-item exit state machine: tracks whether the item is playing
 * its exit animation, and defers the actual removal (via `onDismiss`)
 * until the animation's `EXIT_ANIMATION_DURATION_MS` has elapsed so the
 * transition can be seen before the item unmounts.
 *
 * Shared by both the Tailwind and inline-styles `ToastItem` renderers.
 */
export function useToastItemExit(toastId: string, onDismiss: (id: string) => void): UseToastItemExitReturn {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger exit animation before removing
    return () => {
      setIsExiting(true);
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toastId);
    }, EXIT_ANIMATION_DURATION_MS); // Match animation duration
  }, [toastId, onDismiss]);

  return { isExiting, handleDismiss };
}
